/**
 * iOS Chrome 専用：下部ツールバーの開閉に応じて <html> にクラスを付ける。
 * - is-ios-chrome        … iOS Chrome(CriOS)判定。UAで一度だけ付与。
 * - is-toolbar-collapsed … 下部ツールバーが閉じている（＝可視領域が最大の）間だけ付く。
 *
 * これを使い _common_header.scss で SP の .header__nav をツールバー閉時だけ嵩上げする。
 * iOS Chrome 以外では一切動かない（listener も張らない）。
 * <html> のクラスは swup 遷移で差し替わらない（BodyClassPlugin は body のみ）ので永続で1回だけ仕込む。
 *
 * 判定基準は「大ビューポート高(100lvh＝ツールバー全閉時の高さ)」を実測し、
 * 現在の可視領域(visualViewport.height)がそれとほぼ一致したら「閉」とする。
 * ※ clientHeight や maxトラッキングだと初回(ツールバー展開)で基準が小さく出て誤判定するため lvh を使う。
 */

/** iOS Chrome(CriOS)判定。Android Chrome は "Chrome"、Safari は CriOS 無しなので誤検出しない */
const isIosChrome = (): boolean => /CriOS/.test(navigator.userAgent)

/** 折りたたみ判定の許容差(px)。ツールバー遷移中の端数・サブピクセルを吸収 */
const COLLAPSE_EPS = 20

/** 大ビューポート高(100lvh)の実測値。ツールバー状態に依らない定数（回転で変わるので測り直す） */
let largeViewportH = 0
/** 直近の画面幅。回転などで変わったら 100lvh を測り直す */
let lastWidth = -1
/** rAF 間引き用のハンドル */
let rafId = 0

/**
 * 100lvh（＝ツールバー全閉時の高さ）を隠しプローブで実測してキャッシュする。
 * lvh 未対応の古いiOSでは 0 のままになり、判定は常に「開(余白なし)」にフォールバックする。
 */
const measureLargeViewport = (): void => {
  const probe = document.createElement("div")
  probe.style.cssText = "position:fixed;left:0;top:0;width:0;height:100lvh;visibility:hidden;pointer-events:none"
  document.documentElement.appendChild(probe)
  largeViewportH = probe.getBoundingClientRect().height
  probe.remove()
}

/**
 * 現在の可視領域からツールバー開閉を判定し、is-toolbar-collapsed をトグルする。
 * 可視領域が大ビューポート(100lvh)とほぼ一致＝ツールバーが引っ込んでいる、とみなす。
 */
const updateCollapsed = (): void => {
  const vv = window.visualViewport
  if (!vv) return

  // 画面幅が変わったら（回転など）100lvh を測り直す
  if (window.innerWidth !== lastWidth) {
    lastWidth = window.innerWidth
    measureLargeViewport()
  }

  // lvh 未対応(0)なら判定不能→展開扱い（余白なし）でフォールバック
  const collapsed = largeViewportH > 0 && largeViewportH - vv.height <= COLLAPSE_EPS
  document.documentElement.classList.toggle("is-toolbar-collapsed", collapsed)
}

/** rAF で間引いて updateCollapsed を呼ぶ（scroll/resize の高頻度発火対策） */
const scheduleUpdate = (): void => {
  if (rafId) return
  rafId = requestAnimationFrame(() => {
    rafId = 0
    updateCollapsed()
  })
}

/**
 * 初期化：iOS Chrome のときだけ判定とツールバー監視を開始する。
 */
const initIosChrome = (): void => {
  if (!isIosChrome()) return
  document.documentElement.classList.add("is-ios-chrome")

  const vv = window.visualViewport
  if (vv) {
    vv.addEventListener("resize", scheduleUpdate)
    vv.addEventListener("scroll", scheduleUpdate)
  }
  window.addEventListener("resize", scheduleUpdate)
  window.addEventListener("orientationchange", scheduleUpdate)

  updateCollapsed()
}

initIosChrome()
